import { Box } from '@mui/material';
import { MouseEvent, SyntheticEvent, useEffect, useReducer, useState } from 'react';
import PlusIcon from '../../../../../assets/add-white.svg';
import { initialAlertState, useAlertStatus } from '../../../../../customHook/useAlertStatus';
import { Role } from '../../../../../model/rolesAndPerms.model';
import { modalReducer } from '../../../../../reducers/modalReducer';
import { AdminService } from '../../../../../service/admin.service';
import { useAppDispatch } from '../../../../../store/hooks';
import { fetchRoles, setSelectedRole } from '../../../../../store/slices/rolesAndPermissionsSlice';
import zeplinColor from '../../../../../theme/Color';
import CustomAlert from '../../../../shared/CustomAlert/CustomAlert';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import CustomInputModal from '../../../../shared/CustomInputModal/CustomInputModal';
import CustomSearch from '../../../../shared/CustomSearch/CustomSearch';
import styles from './RolesAndPermission.module.css';
import { RolesGrid } from './components/RolesGrid';
import { RolesTable } from './components/RolesTable';

const enum LayoutType {
  LIST = 'list',
  GRID = 'grid',
}

export const RolesAndPermission = () => {
  const dispatch = useAppDispatch();
  const currentRoleViewType =
    (sessionStorage.getItem('currentRoleViewType') as LayoutType) || LayoutType.GRID;
  const [layout, setLayout] = useState<LayoutType>(currentRoleViewType);
  const [roleCardElement, setRoleCardElement] = useState<SVGSVGElement | null>(null);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    create: false,
  });
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('currentRoleViewType', newValue);
    setLayout(newValue);
  };

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, role: Role) => {
    event.stopPropagation();
    setRoleCardElement(event.currentTarget);
    dispatch(setSelectedRole(role));
  };

  const handleNewRole = async (roleName: string) => {
    try {
      startOperation('upload', 'Adding role...');
      modalDispatch({ type: 'CLOSE', payload: 'create' });
      await AdminService.createRole({ roleName: roleName });
      dispatch(fetchRoles());

      completeOperation('upload', 'Role added successfully');
    } catch (error) {
      completeOperation('upload', `Role Addition Failed: ${error}`, true);
    }
  };

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ width: { sx: '20rem', xl: '25rem' } }}>
          <CustomSearch />
        </Box>
        <Box sx={{ display: 'inline-flex', gap: '1rem' }}>
          <Box
            sx={{
              borderRadius: '50px',
              border: `1px solid ${zeplinColor.Neutral6}`,
              padding: '6px',
              width: { xs: '82px', xl: '92px' },
              height: { xs: '40px', xl: '48px' },
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              backgroundColor: zeplinColor.Neutral1,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: { xs: '35px', xl: '40px' },
                height: { xs: '28px', xl: '36px' },
                backgroundColor: zeplinColor.Brand1,
                opacity: 0.1,
                borderRadius: '40px',
                transition: 'transform 0.3s ease',
                transform:
                  layout === 'grid'
                    ? 'translateX(0)'
                    : { xs: 'translateX(35px)', xl: 'translateX(40px)' },
                left: '6px',
              }}
            />

            <Box
              onClick={(e) => handleLayoutChange(e, LayoutType.GRID)}
              sx={{
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '40px',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={
                  layout === 'grid'
                    ? `/assets/selected-col-layout.svg`
                    : `/assets/column-layout-icon.svg`
                }
                className={layout === 'grid' ? styles['icons'] : styles['selected-icons']}
                alt='Column layout'
                style={{
                  transition: 'all 0.2s ease',
                  transform: layout === 'grid' ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            </Box>

            <Box
              onClick={(e) => handleLayoutChange(e, LayoutType.LIST)}
              sx={{
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '40px',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={
                  layout === 'list'
                    ? `/assets/selected-row-layout.svg`
                    : `/assets/row-layout-icon.svg`
                }
                className={layout === 'list' ? styles['icons'] : styles['selected-icons']}
                alt='Row layout'
                style={{
                  transition: 'all 0.2s ease',
                  transform: layout === 'list' ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            </Box>
          </Box>
          <CustomButton
            disabled={false}
            variant='contained'
            text='New Role'
            onClick={() => modalDispatch({ type: 'OPEN', payload: 'create' })}
            icon={<img src={PlusIcon} alt='white arrow' />}
            iconPosition='start'
            style={{
              borderRadius: '14px',
            }}
            buttonVariant='buttonMedium'
          />
        </Box>
      </Box>
      {layout === LayoutType.GRID ? (
        <RolesGrid
          roleCardElement={roleCardElement}
          handleOpenSettings={handleOpenSettings}
          setRoleCardElement={setRoleCardElement}
        />
      ) : (
        <RolesTable
          roleCardElement={roleCardElement}
          handleOpenSettings={handleOpenSettings}
          setRoleCardElement={setRoleCardElement}
        />
      )}
      <CustomInputModal
        modalType={'create'}
        title={'Create Role'}
        label='Role name'
        isModalOpen={modalState.create}
        modalDispatch={modalDispatch}
        handleAction={handleNewRole}
        inputValue={''}
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};
